import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useUser } from "../context/UserContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  ruta: string;
  menu: string;
  funcion: string;
  /**
   * Ruta a la que redirigir si no tiene permisos
   * Por defecto: "/unauthorized"
   */
  redirectTo?: string;
}

/**
 * Componente de protección de rutas basado en permisos
 *
 * Valida que el usuario tenga el permiso específico antes de renderizar el children.
 * Si no tiene permiso, redirige a la página de "No autorizado" o a la ruta especificada.
 *
 * Características:
 * - Validación asíncrona de permisos con UserContext
 * - Loading state mientras valida
 * - Redirección automática si no autorizado
 * - Soporte para permisos globales "all"
 *
 * @example
 * <Route
 *   path="/usuarios"
 *   element={
 *     <ProtectedRoute ruta="usuarios" menu="gestion-usuarios" funcion="ver-lista">
 *       <UsersPage />
 *     </ProtectedRoute>
 *   }
 * />
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  ruta,
  menu,
  funcion,
  redirectTo = "/unauthorized",
}) => {
  const {
    hasPermission,
    isLoading: userLoading,
    user,
    permissions,
  } = useUser();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      // Esperar a que termine la carga inicial del usuario
      if (userLoading) {
        setChecking(true);
        return;
      }

      // Si no hay usuario después de cargar, no está autenticado
      if (!user) {
        setAuthorized(false);
        setChecking(false);
        return;
      }

      try {
        setChecking(true);

        // Validar permiso específico
        // Si el usuario tiene permisos globales "all", hasPermission retornará true
        const hasAccess = hasPermission(ruta, menu, funcion);

        if (!hasAccess) {
          console.warn(
            `[ProtectedRoute] Acceso denegado a ruta=${ruta}, menu=${menu}, funcion=${funcion}`,
          );
          console.log(
            `[ProtectedRoute] Usuario: ${user?.email}, Permisos cargados: ${permissions.length}`,
          );
        }

        setAuthorized(hasAccess);
      } catch (error) {
        console.error("[ProtectedRoute] Error validando permisos:", error);
        setAuthorized(false);
      } finally {
        setChecking(false);
      }
    };

    checkPermission();
  }, [hasPermission, ruta, menu, funcion, userLoading, user, permissions]);

  // Mostrar loading mientras valida permisos O mientras carga el usuario
  if (checking || userLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Validando permisos...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Redirigir si no tiene permisos
  if (!authorized) {
    console.warn(
      `[ProtectedRoute] Acceso denegado a ruta=${ruta}, menu=${menu}, funcion=${funcion}`,
    );
    return <Navigate to={redirectTo} replace />;
  }

  // Renderizar el componente si está autorizado
  return <>{children}</>;
};
